/**
 * © 2025 Malvys Exaucé Dessou — ZonePro. Tous droits réservés.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { projectsAPI, arrondissementsAPI, addressAPI, lotAPI, regulationsAPI, steThereseAPI } from '../api';
import { useLanguage } from '../context/LanguageContext';
import logger from '../utils/logger';
import { 
  MapPin, 
  ArrowLeft, 
  Search,
  Loader2,
  CheckCircle2,
  Building,
  Home,
  Store,
  Sparkles,
  AlertTriangle,
  HelpCircle,
  Info,
  DollarSign,
  TrendingUp,
  Shield,
  Hash,
  Ruler,
  Layers,
  Play
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import AddressAutocomplete from '../components/AddressAutocomplete';
import RegulatoryAlert from '../components/RegulatoryAlert';

// Status indicator component with enhanced transparency
const DataStatusBadge = ({ status, showTooltip = false, language = 'fr' }) => {
  if (!status) return null;
  
  const configs = {
    confirmed: { 
      label: language === 'fr' ? 'Confirmé' : 'Confirmed', 
      icon: CheckCircle2, 
      className: 'bg-green-100 text-green-700 border-green-300',
      tooltip: language === 'fr' ? 'Données officielles du cadastre du Québec' : 'Official Quebec cadastre data'
    },
    computed: { 
      label: language === 'fr' ? 'Calculé' : 'Computed', 
      icon: Sparkles, 
      className: 'bg-blue-100 text-blue-700 border-blue-300',
      tooltip: language === 'fr' ? 'Calculé à partir de la géométrie cadastrale' : 'Computed from cadastral geometry'
    },
    estimated: { 
      label: language === 'fr' ? 'Estimé' : 'Estimated', 
      icon: AlertTriangle, 
      className: 'bg-yellow-100 text-yellow-700 border-yellow-300',
      tooltip: language === 'fr' ? 'Estimation basée sur les patterns de Montréal' : 'Estimate based on Montreal patterns'
    },
    unavailable: { 
      label: language === 'fr' ? 'Non disponible' : 'Unavailable', 
      icon: HelpCircle, 
      className: 'bg-gray-100 text-gray-500 border-gray-300',
      tooltip: language === 'fr' ? "Donnée non accessible via l'API" : 'Data not accessible via API'
    },
    planning: { 
      label: language === 'fr' ? 'Planification' : 'Planning', 
      icon: Info, 
      className: 'bg-orange-100 text-orange-700 border-orange-300',
      tooltip: language === 'fr' ? "Donnée du Plan d'urbanisme - pas le zonage réglementaire officiel" : 'Urban planning data - not official zoning'
    },
    standard: {
      label: 'Standard',
      icon: CheckCircle2,
      className: 'bg-green-100 text-green-700 border-green-300',
      tooltip: language === 'fr' ? 'Zonage standard applicable' : 'Standard zoning applicable'
    },
    special_sector: {
      label: language === 'fr' ? 'Secteur spécial' : 'Special sector',
      icon: Shield,
      className: 'bg-purple-100 text-purple-700 border-purple-300',
      tooltip: language === 'fr' ? 'Secteur avec réglementation particulière' : 'Sector with special regulations'
    },
    requires_validation: {
      label: language === 'fr' ? 'Validation requise' : 'Validation required',
      icon: AlertTriangle,
      className: 'bg-red-100 text-red-700 border-red-300',
      tooltip: language === 'fr' ? "Résultats à valider avec l'urbanisme" : 'Results need validation with urban planning'
    }
  };
  
  const config = configs[status] || configs.estimated;
  const Icon = config.icon;
  
  return (
    <Badge 
      variant="outline" 
      className={`text-xs ${config.className}`}
      title={showTooltip ? config.tooltip : undefined}
    >
      <Icon className="w-3 h-3 mr-1" />
      {config.label}
    </Badge>
  );
};

// Dimension confidence indicator component
const DimensionConfidenceBadge = ({ confidence, method }) => {
  if (!confidence) return null;
  
  const configs = {
    high: {
      label: 'Fiable',
      className: 'bg-green-50 text-green-700 border-green-200',
      description: 'Lot rectangulaire détecté',
      icon: CheckCircle2
    },
    medium: {
      label: 'Approximatif',
      className: 'bg-yellow-50 text-yellow-700 border-yellow-200',
      description: 'Lot irrégulier',
      icon: AlertTriangle
    },
    low: {
      label: 'Indicatif',
      className: 'bg-orange-50 text-orange-700 border-orange-200',
      description: 'Géométrie complexe',
      icon: Info
    },
    unknown: {
      label: 'Non calculé',
      className: 'bg-gray-50 text-gray-500 border-gray-200',
      description: 'Données insuffisantes',
      icon: HelpCircle
    }
  };
  
  const config = configs[confidence] || configs.unknown;
  const Icon = config.icon;
  
  const methodLabels = {
    'mbr_rectangular_lot': 'Rectangle englobant minimal',
    'mbr_irregular_lot': 'Approximation MBR',
    'mbr_complex_polygon': 'Polygone complexe',
    'insufficient_vertices': 'Géométrie insuffisante'
  };
  
  return (
    <div 
      className={`flex items-center gap-1.5 px-2 py-1 rounded-md border text-xs ${config.className}`}
      title={`${config.description}${method ? ` (${methodLabels[method] || method})` : ''}`}
    >
      <Icon className="w-3 h-3" />
      <span>Dimensions: {config.label}</span>
    </div>
  );
};

const projectTypes = [
  { value: 'residential', label: 'Résidentiel', icon: Home, description: 'Habitations' },
  { value: 'mixed_use', label: 'Mixte', icon: Building, description: 'Commerce + Résidentiel' },
  { value: 'commercial', label: 'Commercial', icon: Store, description: 'Bureaux, commerces' }
];

const lotShapes = [
  { value: 'rectangular', label: 'Rectangulaire' },
  { value: 'irregular', label: 'Irrégulier' },
  { value: 'triangular', label: 'Triangulaire' },
  { value: 'l_shaped', label: 'En L' }
];

// Density level badge colors
const densityColors = {
  low: 'bg-blue-100 text-blue-700',
  low_medium: 'bg-cyan-100 text-cyan-700',
  medium: 'bg-yellow-100 text-yellow-700',
  medium_high: 'bg-orange-100 text-orange-700',
  high: 'bg-red-100 text-red-700'
};

const densityLabels = {
  low: 'Faible',
  low_medium: 'Faible-Moy.',
  medium: 'Moyenne',
  medium_high: 'Moy.-Haute',
  high: 'Haute'
};

// ZonePro Disclaimer Component
const ZoneProDisclaimer = () => (
  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
    <div className="flex items-start gap-2">
      <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
      <div>
        <p className="font-medium">Avis important</p>
        <p className="mt-1 text-amber-700">
          ZonePro fournit une analyse préliminaire du potentiel de développement basée sur les données disponibles. 
          Les résultats ne sont pas juridiquement contraignants et ne remplacent pas une consultation professionnelle 
          avec l&apos;arrondissement ou un urbaniste agréé. La fiabilité des données est indiquée pour chaque champ.
        </p>
      </div>
    </div>
  </div>
);

// Coverage Indicator Component
const CoverageIndicator = ({ arrondissement }) => {
  // Don't show anything if arrondissement is not yet determined
  if (!arrondissement) return null;
  
  // Normalize comparison - handle both en-dash (–) and hyphen (-)
  const normalizedArr = arrondissement.toLowerCase().replace(/[–—-]/g, '-');
  const isRosemont = normalizedArr.includes('rosemont');
  
  return (
    <div className={`flex items-center gap-2 p-2 rounded-lg text-sm ${
      isRosemont 
        ? 'bg-green-50 border border-green-200 text-green-700' 
        : 'bg-orange-50 border border-orange-200 text-orange-700'
    }`}>
      {isRosemont ? (
        <>
          <CheckCircle2 className="w-4 h-4" />
          <span><strong>Zonage réglementaire haute précision</strong> — {arrondissement}</span>
        </>
      ) : (
        <>
          <Info className="w-4 h-4" />
          <span><strong>Données de planification</strong> — Zonage réglementaire non disponible pour {arrondissement}</span>
        </>
      )}
    </div>
  );
};

// A lot's address as the project stores it, its count of roll units included (REQ-39)
const storedAddress = (addr) => ({
  address_formatted: addr.address_formatted,
  civique_debut: addr.civique_debut,
  civique_fin: addr.civique_fin,
  nom_rue: addr.nom_rue,
  superficie_terrain: addr.superficie_terrain,
  matricule: addr.matricule,
  source: addr.source,
  confidence: addr.confidence,
  units: addr.units
});

// Planning Data Warning Component
const PlanningDataWarning = ({ zoningData, language = 'fr' }) => {
  if (!zoningData?.planning_warning) return null;
  
  return (
    <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm text-orange-700 flex items-start gap-2">
      <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
      <span>{zoningData.planning_warning}</span>
    </div>
  );
};

const NewProject = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [arrondissements, setArrondissements] = useState([]);
  const [selectedArrProfile, setSelectedArrProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const [addressLookedUp, setAddressLookedUp] = useState(false);
  const [dataStatus, setDataStatus] = useState({});  // Track data source status
  const [regulatoryContext, setRegulatoryContext] = useState(null);  // Regulatory alerts
  const [dimensionConfidence, setDimensionConfidence] = useState({ confidence: null, method: null });  // Dimension reliability
  const [zoningData, setZoningData] = useState(null);  // Store zoning data for warnings
  
  // NEW: Lot lookup state
  const [lotNumber, setLotNumber] = useState('');
  const [addressSearch, setAddressSearch] = useState('');
  const [lotData, setLotData] = useState(null);  // Data from lot lookup
  const [lotLookedUp, setLotLookedUp] = useState(false);
  const [analysisReady, setAnalysisReady] = useState(false);
  
  // Sainte-Thérèse : confirmation de zone quand le terrain touche une limite (options)
  const [stZoneChoice, setStZoneChoice] = useState(null);  // {options, message}
  
  // Sector resolution state (adresse -> secteur via carte officielle)
  const [pendingProject, setPendingProject] = useState(null);  // project data waiting for sector confirmation
  // Montréal : aucun secteur ne couvre 60 % du terrain, l'utilisateur choisit parmi ceux nommés (spec 121, Q7)
  const [secteurChoice, setSecteurChoice] = useState(null);  // {options, message}
  
  const [formData, setFormData] = useState({
    name: '',
    project_type: 'residential',
    description: '',
    property: {
      address: '',
      municipality: 'Montréal',
      arrondissement: '',
      zone_code: '',
      lot_area: '',
      lot_width: '',
      lot_depth: '',
      lot_shape: 'rectangular',
      cadastral_number: '',
      lot_number: '',
      latitude: null,
      longitude: null,
      geometry: null,
      geometry_type: null
    }
  });

  const loadArrondissements = useCallback(async () => {
    try {
      const response = await arrondissementsAPI.list();
      // Sort by sale_price_sqm descending (most expensive first)
      const sorted = (response.data.arrondissements || []).sort(
        (a, b) => b.sale_price_sqm - a.sale_price_sqm
      );
      setArrondissements(sorted);
    } catch (error) {
      logger.error('Error loading arrondissements:', error);
      toast.error('Erreur lors du chargement des arrondissements');
    }
  }, []);

  useEffect(() => {
    loadArrondissements();
  }, [loadArrondissements]);

  // Update selected arrondissement profile when arrondissement changes
  const handleArrondissementChange = (value) => {
    handleChange('property.arrondissement', value);
    const profile = arrondissements.find(a => a.name === value);
    setSelectedArrProfile(profile || null);
  };

  const handleChange = (field, value) => {
    if (field.startsWith('property.')) {
      const propertyField = field.replace('property.', '');
      setFormData({
        ...formData,
        property: { ...formData.property, [propertyField]: value }
      });
    } else {
      setFormData({ ...formData, [field]: value });
    }
  };

  // Sainte-Thérèse : créer le projet depuis une réponse adresse/lot (zone résolue par géométrie dérivée)
  const handleSteThereseData = async (data, sourceLabel) => {
    if (!data.lot_area) {
      toast.error(language === 'fr'
        ? "Dimensions du terrain non disponibles (cadastre du Québec injoignable) — réessayez plus tard"
        : 'Lot dimensions unavailable (Quebec cadastre unreachable) — please try again later');
      return;
    }
    const projectData = {
      name: `${language === 'fr' ? 'Projet' : 'Project'} ${sourceLabel} (Sainte-Thérèse)`,
      project_type: 'residential',
      description: '',
      property: {
        address: data.matched_address || data.primary_address || '',
        municipality: data.municipality,
        arrondissement: '',
        zone_code: data.zone_code || '',
        lot_area: parseFloat(data.lot_area),
        lot_width: parseFloat(data.lot_width) || 0,
        lot_depth: parseFloat(data.lot_depth) || 0,
        lot_shape: 'rectangular',
        cadastral_number: data.lot_number || data.cadastral_number || '',
        latitude: data.latitude ?? data.centroid_lat ?? null,
        longitude: data.longitude ?? data.centroid_lng ?? null,
        geometry: data.geometry,
        geometry_type: data.geometry_type,
        dimension_confidence: data.dimension_confidence,
        dimension_method: data.dimension_method,
        data_source: 'Cadastre du Québec (MERN)',
        zoning_data_type: 'regulatory',
        zoning_data_source: 'Règlement de zonage 1200 N.S.',
        zone_resolution: data.zone_resolution || null
      }
    };
    if (data.zone_resolution?.status === 'ambiguous') {
      setPendingProject(projectData);
      setStZoneChoice({ options: data.zone_options || [], message: data.zone_resolution.message });
      toast.warning(language === 'fr' ? 'Le terrain touche une limite de zones' : 'The land touches a zone boundary', {
        description: language === 'fr' ? 'Veuillez confirmer la zone applicable' : 'Please confirm the applicable zone'
      });
      return;
    }
    toast.success(language === 'fr'
      ? `Zone ${data.zone_code} déterminée automatiquement (géométrie dérivée de la carte officielle JMap)`
      : `Zone ${data.zone_code} automatically determined (geometry derived from the official JMap map)`);
    const projectResponse = await projectsAPI.create(projectData);
    navigate(`/projects/${projectResponse.data.id}`);
  };

  // Sainte-Thérèse : créer le projet avec la zone confirmée par l'utilisateur (terrain sur une limite)
  const createProjectWithStZone = async (zone) => {
    if (!pendingProject) return;
    setLoading(true);
    try {
      const projectData = {
        ...pendingProject,
        property: {
          ...pendingProject.property,
          zone_code: zone,
          zone_resolution: {
            ...(pendingProject.property.zone_resolution || {}),
            status: 'resolved',
            chosen_from_options: true
          }
        }
      };
      const projectResponse = await projectsAPI.create(projectData);
      toast.success(language === 'fr' ? `Zone ${zone} confirmée` : `Zone ${zone} confirmed`);
      navigate(`/projects/${projectResponse.data.id}`);
    } catch (error) {
      logger.error('ST zone confirm error:', error);
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur lors de la création' : 'Error creating project'));
    } finally {
      setLoading(false);
    }
  };

  // Montréal, adresse ou lot : la réponse « ambiguë » nomme les secteurs ; on attend le choix (REQ-38).
  // Vrai quand la réponse attend ce choix, et le projet n'est alors pas créé.
  const awaitSecteurChoice = (data, projectData) => {
    if (data.sector_resolution?.status !== 'ambiguous' || !(data.sector_options || []).length) return false;
    setPendingProject(projectData);
    setSecteurChoice({ options: data.sector_options, message: data.sector_resolution.message });
    toast.warning(language === 'fr' ? 'Le terrain est partagé entre plusieurs secteurs' : 'The land is shared between several sectors', {
      description: language === 'fr' ? 'Veuillez confirmer le secteur applicable' : 'Please confirm the applicable sector'
    });
    return true;
  };

  // Créer le projet au secteur choisi : sa zone vient de sa fiche officielle, comme pour un secteur identifié
  const createProjectWithSecteur = async (secteur) => {
    if (!pendingProject) return;
    setLoading(true);
    try {
      const sheet = await regulationsAPI.getBySector(secteur);
      const projectData = {
        ...pendingProject,
        property: { ...pendingProject.property, sector_number: secteur, zone_code: sheet.data.zone_code || '' }
      };
      const projectResponse = await projectsAPI.create(projectData);
      toast.success(language === 'fr' ? `Secteur ${secteur} confirmé` : `Sector ${secteur} confirmed`);
      navigate(`/projects/${projectResponse.data.id}`);
    } catch (error) {
      logger.error('Secteur confirm error:', error);
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur lors de la création' : 'Error creating project'));
    } finally {
      setLoading(false);
    }
  };

  // NEW: Lot Number Lookup Function - Creates project and redirects to analysis
  const lookupLotNumber = async () => {
    if (!lotNumber || lotNumber.trim().length < 5) {
      toast.error(language === 'fr' ? 'Veuillez entrer un numéro de lot valide (ex: 1 588 564)' : 'Please enter a valid lot number (e.g., 1 588 564)');
      return;
    }
    
    setLookingUp(true);
    setSecteurChoice(null);
    
    try {
      // Step 1: Lookup lot data
      const response = await lotAPI.lookup(lotNumber);
      const data = response.data;
      
      // Sainte-Thérèse : parcours dédié (zone résolue par géométrie dérivée de la carte officielle)
      if (data.municipality === 'Sainte-Thérèse') {
        await handleSteThereseData(data, `Lot ${data.lot_number}`);
        return;
      }
      
      if (!data.lot_area || (!data.zone_code && !data.sector_resolution)) {
        toast.error(language === 'fr' ? "Données insuffisantes pour lancer l'analyse" : 'Insufficient data to start analysis');
        setLookingUp(false);
        return;
      }
      
      // Step 2: Create project directly with lot data
      // Use primary_address from reverse geocoding first, then fallback to associated_addresses
      const primaryAddress = data.primary_address || data.associated_addresses?.[0]?.address_formatted || '';
      
      // Format associated addresses for storage
      const associatedAddresses = (data.associated_addresses || []).map(storedAddress);
      
      const projectData = {
        name: `${language === 'fr' ? 'Projet Lot' : 'Project Lot'} ${data.lot_number}`,
        project_type: 'residential',
        description: '',
        property: {
          address: primaryAddress,
          municipality: data.municipality,
          arrondissement: data.arrondissement || '',
          zone_code: data.zone_code || '',
          zone_type: data.zoning_data?.zone_type || '',
          lot_area: parseFloat(data.lot_area),
          lot_width: parseFloat(data.lot_width) || 0,
          lot_depth: parseFloat(data.lot_depth) || 0,
          lot_shape: 'rectangular',
          cadastral_number: data.lot_number,
          lot_number: data.lot_number,
          latitude: data.centroid_lat,
          longitude: data.centroid_lng,
          geometry: data.geometry,
          geometry_type: data.geometry_type,
          // Secteur de zonage résolu via la carte officielle
          sector_number: data.sector_number || null,
          // Une zone lue par fiche (le Plateau, spec 127) : son territoire et sa résolution
          jurisdiction_id: data.jurisdiction_id || null,
          zone_resolution: data.zone_resolution || null,
          // Enhanced data fields
          associated_addresses: associatedAddresses,
          dimension_confidence: data.dimension_confidence,
          dimension_method: data.dimension_method,
          data_source: data.data_sources?.cadastre || 'Cadastre du Québec',
          zoning_data_type: data.zoning_data?.data_type || 'planning',
          zoning_data_source: data.zoning_data?.data_source || "Plan d'urbanisme de Montréal"
        }
      };
      
      // Une zone non résolue (inconnue ou ambiguë) : on le dit, on ne devine pas (spec 127, comme le secteur)
      if (data.zone_resolution && data.zone_resolution.status !== 'resolved') {
        setLookingUp(false);
        toast.error(data.zone_resolution.message);
        return;
      }
      if (data.jurisdiction_id && data.zone_code) {
        toast.success(language === 'fr' ? `Zone ${data.zone_code} identifiée (carte officielle)` : `Zone ${data.zone_code} identified (official map)`);
      }

      // Gestion de la résolution du secteur (Rosemont)
      if (awaitSecteurChoice(data, projectData)) return;
      if (data.sector_resolution && data.sector_resolution.status !== 'resolved') {
        // Secteur inconnu : on le dit, on ne devine pas et on ne fait pas saisir (décision 2026-09-26).
        setLookingUp(false);
        toast.error(data.sector_resolution.message);
        return;
      }

      if (data.sector_number) {
        toast.success(language === 'fr'
          ? `Secteur de zonage ${data.sector_number} identifié (carte officielle)`
          : `Zoning sector ${data.sector_number} identified (official map)`);
      }

      const projectResponse = await projectsAPI.create(projectData);
      toast.success(t('newProject.analysisLaunched', { lot: data.lot_number }));
      
      // Step 3: Redirect directly to project analysis page
      navigate(`/projects/${projectResponse.data.id}`);
      
    } catch (error) {
      logger.error('Lot lookup/create error:', error);
      // Handle Pydantic validation errors (array of objects) or simple string errors
      const errorDetail = error.response?.data?.detail;
      let errorMessage = language === 'fr' ? 'Erreur lors de la recherche du lot' : 'Error looking up lot';
      
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        // Pydantic validation error - extract first message
        errorMessage = errorDetail[0]?.msg || errorDetail[0]?.message || (language === 'fr' ? 'Erreur de validation' : 'Validation error');
      } else if (errorDetail?.msg) {
        errorMessage = errorDetail.msg;
      }
      
      toast.error(errorMessage);
    } finally {
      setLookingUp(false);
    }
  };

  // Handle manual arrondissement correction
  const handleArrondissementCorrection = (newArrondissement) => {
    setFormData({
      ...formData,
      property: { ...formData.property, arrondissement: newArrondissement }
    });
    
    // Update lotData if exists
    if (lotData) {
      setLotData({ ...lotData, arrondissement: newArrondissement });
    }
    
    // Re-check if this is Rosemont for regulatory data
    const isRosemont = newArrondissement.toLowerCase().includes('rosemont');
    if (!isRosemont && lotData?.zoning_data) {
      setZoningData({
        ...lotData.zoning_data,
        data_type: 'planning',
        planning_warning: '⚠️ Données de planification uniquement pour cet arrondissement'
      });
    }
  };

  // Address Lookup - Creates project and redirects to analysis (même parcours que le lot)
  const lookupAddressAndCreate = async (addr) => {
    const addressToUse = (addr || '').trim();
    if (!addressToUse || addressToUse.length < 5) {
      toast.error(language === 'fr' ? 'Veuillez entrer une adresse valide' : 'Please enter a valid address');
      return;
    }
    
    setLookingUp(true);
    setSecteurChoice(null);
    
    try {
      const response = await addressAPI.lookup(addressToUse);
      const data = response.data;
      
      if (data.error) {
        toast.error(data.message || (language === 'fr' ? 'Adresse non trouvée' : 'Address not found'));
        return;
      }
      
      // Sainte-Thérèse : parcours dédié (zone résolue par géométrie dérivée de la carte officielle)
      if (data.municipality === 'Sainte-Thérèse') {
        await handleSteThereseData(data, (data.matched_address || addressToUse).split(',')[0]);
        return;
      }
      
      if (!data.lot_area) {
        toast.error(language === 'fr' 
          ? "Dimensions du terrain non disponibles (cadastre du Québec injoignable) — réessayez plus tard" 
          : 'Lot dimensions unavailable (Quebec cadastre unreachable) — please try again later');
        return;
      }
      
      const projectData = {
        name: `${language === 'fr' ? 'Projet' : 'Project'} ${addressToUse.split(',')[0]}`,
        project_type: 'residential',
        description: '',
        property: {
          address: data.matched_address || addressToUse,
          municipality: data.municipality,
          arrondissement: data.arrondissement || '',
          zone_code: data.zone_code || '',
          lot_area: parseFloat(data.lot_area),
          lot_width: parseFloat(data.lot_width) || 0,
          lot_depth: parseFloat(data.lot_depth) || 0,
          lot_shape: data.lot_shape || 'rectangular',
          cadastral_number: data.cadastral_number || '',
          lot_number: data.lot_number || '',
          latitude: data.latitude,
          longitude: data.longitude,
          geometry: data.geometry,
          geometry_type: data.geometry_type,
          // Secteur de zonage résolu via la carte officielle
          sector_number: data.sector_number || null,
          // Une zone lue par fiche (le Plateau, spec 127) : son territoire et sa résolution
          jurisdiction_id: data.jurisdiction_id || null,
          zone_resolution: data.zone_resolution || null,
          // Copropriété divise : agrégat au niveau de l'immeuble
          coproperty: data.coproperty || null,
          // Plage d'adresses de la fiche du rôle (ex: 6532-6534 rue de Bordeaux)
          associated_addresses: (data.associated_addresses || []).map(storedAddress),
          dimension_confidence: data.dimension_confidence,
          dimension_method: data.dimension_method,
          data_source: data.data_source || '',
          zoning_data_type: data.zoning_data?.data_type || 'planning',
          zoning_data_source: data.zoning_data?.data_source || "Plan d'urbanisme de Montréal"
        }
      };
      
      // Une zone non résolue (inconnue ou ambiguë) : on le dit, on ne devine pas (spec 127, comme le secteur)
      if (data.zone_resolution && data.zone_resolution.status !== 'resolved') {
        setLookingUp(false);
        toast.error(data.zone_resolution.message);
        return;
      }
      if (data.jurisdiction_id && data.zone_code) {
        toast.success(language === 'fr' ? `Zone ${data.zone_code} identifiée (carte officielle)` : `Zone ${data.zone_code} identified (official map)`);
      }

      // Gestion de la résolution du secteur (Rosemont)
      if (awaitSecteurChoice(data, projectData)) return;
      if (data.sector_resolution && data.sector_resolution.status !== 'resolved') {
        // Secteur inconnu : on le dit, on ne devine pas et on ne fait pas saisir (décision 2026-09-26).
        setLookingUp(false);
        toast.error(data.sector_resolution.message);
        return;
      }

      if (data.sector_number) {
        toast.success(language === 'fr'
          ? `Secteur de zonage ${data.sector_number} identifié (carte officielle)`
          : `Zoning sector ${data.sector_number} identified (official map)`);
      }

      const projectResponse = await projectsAPI.create(projectData);
      toast.success(language === 'fr' ? 'Analyse lancée' : 'Analysis launched');
      navigate(`/projects/${projectResponse.data.id}`);
      
    } catch (error) {
      logger.error('Address lookup/create error:', error);
      const errorDetail = error.response?.data?.detail;
      toast.error(typeof errorDetail === 'string' ? errorDetail : (language === 'fr' ? "Erreur lors de la recherche de l'adresse" : 'Error looking up address'));
    } finally {
      setLookingUp(false);
    }
  };

  const handleSubmit = async () => {
    // Validate: need either address OR lot_number, plus lot_area
    const hasIdentifier = formData.property.address || formData.property.lot_number;
    if (!hasIdentifier || !formData.property.lot_area) {
      toast.error('Veuillez compléter les informations du terrain');
      return;
    }
    
    setLoading(true);
    try {
      const submitData = {
        ...formData,
        name: formData.name || `Projet ${formData.property.address}`,
        property: {
          ...formData.property,
          lot_area: parseFloat(formData.property.lot_area),
          lot_width: parseFloat(formData.property.lot_width),
          lot_depth: parseFloat(formData.property.lot_depth)
        }
      };
      
      const response = await projectsAPI.create(submitData);
      toast.success('Projet créé avec succès');
      navigate(`/projects/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8 max-w-5xl mx-auto" data-testid="new-project-page">
      {/* Header */}
      <div className="mb-8">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/dashboard')}
          className="mb-4"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('common.back')}
        </Button>
        <h1 className="font-heading font-bold text-3xl uppercase text-primary">
          {language === 'fr' ? 'Nouvelle analyse' : 'New Analysis'}
        </h1>
        <p className="text-muted-foreground">
          {language === 'fr' 
            ? "Entrez une adresse ou un numéro de lot cadastral pour obtenir une analyse de faisabilité"
            : "Enter an address or a cadastral lot number to get a feasibility analysis"
          }
        </p>
      </div>

      {/* Main Lot Number Input Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <Card className="mb-6 border-l-4 border-l-accent">
          <CardHeader>
            <CardTitle className="font-heading font-bold flex items-center gap-2">
              <Search className="w-5 h-5 text-accent" />
              {language === 'fr' ? 'Identifier le terrain' : 'Identify the land'}
            </CardTitle>
            <CardDescription>
              {language === 'fr' 
                ? "Recherchez par adresse civique ou par numéro de lot du cadastre du Québec"
                : "Search by civic address or by Quebec cadastre lot number"
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="address">
              <TabsList className="grid w-full grid-cols-3 mb-4">
                <TabsTrigger value="address" data-testid="search-tab-address">
                  <MapPin className="w-4 h-4 mr-2" />
                  {language === 'fr' ? 'Adresse civique' : 'Civic address'}
                </TabsTrigger>
                <TabsTrigger value="lot" data-testid="search-tab-lot">
                  <Hash className="w-4 h-4 mr-2" />
                  {language === 'fr' ? 'Numéro de lot' : 'Lot number'}
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="address">
                <div className="flex gap-4">
                  <div className="flex-1">
                    <AddressAutocomplete
                      value={addressSearch}
                      onChange={setAddressSearch}
                      onSelect={(d) => lookupAddressAndCreate(d?.address || d?.selectedText || addressSearch)}
                      onSubmitText={lookupAddressAndCreate}
                      placeholder={language === 'fr' ? 'Ex: 6532 rue de Bordeaux, Montréal' : 'E.g.: 6532 rue de Bordeaux, Montreal'}
                      disabled={lookingUp}
                      data-testid="address-search-input"
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      {language === 'fr' 
                        ? "Le secteur de zonage est identifié automatiquement sur la carte officielle de la Ville."
                        : "The zoning sector is automatically identified on the City's official map."
                      }
                    </p>
                  </div>
                  <Button
                    onClick={() => lookupAddressAndCreate(addressSearch)}
                    disabled={lookingUp || !addressSearch.trim()}
                    className="bg-accent text-primary hover:bg-accent/90 px-6"
                    data-testid="lookup-address-btn"
                  >
                    {lookingUp ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Search className="w-4 h-4 mr-2" />
                    )}
                    {t('common.search').toUpperCase()}
                  </Button>
                </div>
              </TabsContent>
              
              <TabsContent value="lot">
                <div className="flex items-center gap-2 mb-2 text-sm text-muted-foreground">
                  <span>{language === 'fr' ? 'Numéro de lot du cadastre du Québec (ex: 1 588 564 ou 1588564)' : 'Quebec cadastre lot number (e.g., 1 588 564 or 1588564)'}</span>
                  <div className="relative group">
                    <HelpCircle className="w-4 h-4 text-muted-foreground cursor-help" />
                    <div className="absolute left-0 top-6 z-50 hidden group-hover:block w-64 p-3 bg-popover border rounded-lg shadow-lg text-sm text-popover-foreground">
                      <p className="font-medium mb-2">{language === 'fr' ? 'Où trouver ce numéro ?' : 'Where to find this number?'}</p>
                      <ul className="space-y-1 text-xs">
                        <li>• {language === 'fr' ? "L'acte de vente (titre de propriété)" : "The deed of sale (property title)"}</li>
                        <li>• {language === 'fr' ? "Le compte de taxes" : "The tax bill"}</li>
                        <li>• {language === 'fr' ? "Le certificat de localisation" : "The certificate of location"}</li>
                        <li>• <a href="https://appli.mern.gouv.qc.ca/Infolot/" target="_blank" rel="noopener noreferrer" className="text-accent underline">Infolot (MERN)</a></li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div className="flex gap-4">
              <div className="flex-1">
                <Input
                  value={lotNumber}
                  onChange={(e) => setLotNumber(e.target.value)}
                  placeholder={t('newProject.lotPlaceholder')}
                  className="text-lg font-mono"
                  data-testid="lot-number-input"
                  onKeyDown={(e) => e.key === 'Enter' && lookupLotNumber()}
                />
                {/* Warning for condo lots (PC-xxxxx) */}
                {lotNumber.toUpperCase().startsWith('PC-') && (
                  <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-amber-800 dark:text-amber-300">
                        {language === 'fr' ? 'Lot de copropriété (condo)' : 'Condo lot'}
                      </p>
                      <p className="text-amber-700 dark:text-amber-400/80 mt-1">
                        {language === 'fr' 
                          ? "Les dimensions récupérées représenteront le lot-mère (terrain complet de l'immeuble), et non l'unité individuelle."
                          : "The dimensions retrieved will represent the mother lot (entire building land), not the individual unit."
                        }
                      </p>
                    </div>
                  </div>
                )}
              </div>
              <Button
                onClick={lookupLotNumber}
                disabled={lookingUp || !lotNumber.trim()}
                className="bg-accent text-primary hover:bg-accent/90 px-6"
                data-testid="lookup-lot-btn"
              >
                {lookingUp ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Search className="w-4 h-4 mr-2" />
                )}
                {t('common.search').toUpperCase()}
              </Button>
            </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </motion.div>

      {/* Loading State - During Lot Lookup */}
      {lookingUp && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-16"
        >
          <Loader2 className="w-12 h-12 animate-spin text-accent mx-auto mb-4" />
          <h3 className="font-heading text-xl text-muted-foreground mb-2">
            {language === 'fr' ? 'Analyse en cours...' : 'Analysis in progress...'}
          </h3>
          <p className="text-muted-foreground max-w-md mx-auto">
            {language === 'fr' 
              ? "Récupération des données cadastrales et identification du secteur de zonage sur la carte officielle de la Ville (jusqu'à 30 secondes)."
              : "Retrieving cadastral data and identifying the zoning sector on the City's official map (up to 30 seconds)."
            }
          </p>
        </motion.div>
      )}

      {/* Sainte-Thérèse : confirmation de zone (terrain sur une limite de zones) */}
      {pendingProject && stZoneChoice && !lookingUp && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="mb-6 border-l-4 border-l-amber-500" data-testid="st-zone-confirmation-card">
            <CardHeader>
              <CardTitle className="font-heading font-bold flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                {language === 'fr' ? 'Zone de zonage à confirmer' : 'Zoning zone to confirm'}
              </CardTitle>
              <CardDescription data-testid="st-zone-resolution-message">
                {stZoneChoice.message || (language === 'fr'
                  ? 'Le terrain touche une limite de zones sur la carte officielle. Sélectionnez la zone applicable.'
                  : 'The land touches a zone boundary on the official map. Select the applicable zone.')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-3">
                {(stZoneChoice.options || []).map((z) => (
                  <Button
                    key={z}
                    onClick={() => createProjectWithStZone(z)}
                    disabled={loading}
                    className="bg-accent text-primary hover:bg-accent/90 font-mono"
                    data-testid={`st-zone-option-${z}`}
                  >
                    Zone {z}
                  </Button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                {language === 'fr'
                  ? "Comme sur la carte officielle de la Ville, un point à cheval sur une limite peut appartenir à plusieurs zones — la zone exacte doit être confirmée."
                  : 'As on the official City map, a point on a boundary can belong to several zones — the exact zone must be confirmed.'}
              </p>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Montréal : terrain partagé entre secteurs, aucun ne couvre 60 % (spec 121, Q7) */}
      {pendingProject && secteurChoice && !lookingUp && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="mb-6 border-l-4 border-l-amber-500" data-testid="secteur-choice-card">
            <CardHeader>
              <CardTitle className="font-heading font-bold flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                {language === 'fr' ? 'Secteur de zonage à confirmer' : 'Zoning sector to confirm'}
              </CardTitle>
              <CardDescription data-testid="secteur-resolution-message">{secteurChoice.message}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-3">
                {secteurChoice.options.map((s) => (
                  <Button
                    key={s}
                    onClick={() => createProjectWithSecteur(s)}
                    disabled={loading}
                    className="bg-accent text-primary hover:bg-accent/90 font-mono"
                    data-testid={`secteur-option-${s}`}
                  >
                    {language === 'fr' ? 'Secteur' : 'Sector'} {s}
                  </Button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                {language === 'fr'
                  ? 'Le choix ouvre la fiche officielle du secteur choisi.'
                  : 'The choice opens the official fiche of the chosen sector.'}
              </p>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Initial State - Before Lot Lookup */}
      {!lotLookedUp && !lookingUp && !pendingProject && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-12"
        >
          <div className="w-20 h-20 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Hash className="w-10 h-10 text-accent" />
          </div>
          <h3 className="font-heading text-xl text-muted-foreground mb-2">
            {language === 'fr' ? 'Commencez par entrer une adresse ou un numéro de lot' : 'Start by entering an address or a lot number'}
          </h3>
          <p className="text-muted-foreground max-w-md mx-auto">
            {language === 'fr' 
              ? "Nous lancerons automatiquement l'analyse complète du terrain."
              : "We will automatically launch the complete land analysis."
            }
          </p>
        </motion.div>
      )}
    </div>
  );
};

export default NewProject;
